import { Schema } from "effect"

// An indicator's style, as TradingView's Style tab edits it: per plot, level, and
// fill, plus the output options. Only what differs from the indicator's defaults
// is stored, so better defaults still reach saved charts.

/** "#RRGGBB" or "#RRGGBBAA" (alpha = opacity), upper case: the only colour form stored. */
export const Color = Schema.String.pipe(Schema.check(Schema.isPattern(/^#[0-9A-F]{6}([0-9A-F]{2})?$/)))

export const LINE_WIDTHS = [1, 2, 3, 4] as const
export type LineWidth = (typeof LINE_WIDTHS)[number]
export const LINE_STYLES = ["solid", "dashed", "dotted"] as const
export type LineStyleName = (typeof LINE_STYLES)[number]

/** Pine's plot types, as TradingView's Style tab offers them. */
export const PLOT_TYPES = [
  "line",
  "linebr",
  "stepline",
  "steplinebr",
  "stepline_diamond",
  "histogram",
  "cross",
  "area",
  "areabr",
  "columns",
  "circles"
] as const
export type PlotType = (typeof PLOT_TYPES)[number]

const PlotStyle = Schema.Struct({
  visible: Schema.optionalKey(Schema.Boolean),
  color: Schema.optionalKey(Color),
  lineWidth: Schema.optionalKey(Schema.Literals(LINE_WIDTHS)),
  lineStyle: Schema.optionalKey(Schema.Literals(LINE_STYLES)),
  type: Schema.optionalKey(Schema.Literals(PLOT_TYPES)),
  /** Per-bar colours (e.g. MACD's histogram), keyed by their index in the plot's palette. */
  palette: Schema.optionalKey(Schema.Record(Schema.String, Color))
})
export type PlotStyle = typeof PlotStyle.Type

const LevelStyle = Schema.Struct({
  visible: Schema.optionalKey(Schema.Boolean),
  color: Schema.optionalKey(Color),
  lineWidth: Schema.optionalKey(Schema.Literals(LINE_WIDTHS)),
  lineStyle: Schema.optionalKey(Schema.Literals(LINE_STYLES)),
  price: Schema.optionalKey(Schema.Finite)
})
export type LevelStyle = typeof LevelStyle.Type

const FillStyle = Schema.Struct({
  visible: Schema.optionalKey(Schema.Boolean),
  color: Schema.optionalKey(Color)
})
export type FillStyle = typeof FillStyle.Type

export const IndicatorStyle = Schema.Struct({
  /** By plot id ("plot0"). */
  plots: Schema.optionalKey(Schema.Record(Schema.String, PlotStyle)),
  /** By level (hline) id. */
  levels: Schema.optionalKey(Schema.Record(Schema.String, LevelStyle)),
  /** By fill id. */
  fills: Schema.optionalKey(Schema.Record(Schema.String, FillStyle)),
  /** Decimals shown; absent: the indicator's own format, else the symbol's. */
  precision: Schema.optionalKey(Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 8 })))),
  /** TradingView's "Labels on price scale" (on by default). */
  labelsOnPriceScale: Schema.optionalKey(Schema.Boolean),
  /** TradingView's "Values in status line" (on by default). */
  valuesInStatusLine: Schema.optionalKey(Schema.Boolean),
  /** TradingView's "Inputs in status line" (on by default). */
  inputsInStatusLine: Schema.optionalKey(Schema.Boolean)
})
export type IndicatorStyle = typeof IndicatorStyle.Type

/** A style from the settings dialog, checked before it's stored (null: not valid). */
export const decodeIndicatorStyle = (style: unknown): IndicatorStyle | null => {
  const decoded = Schema.decodeUnknownOption(IndicatorStyle)(style)
  return decoded._tag === "Some" ? decoded.value : null
}
